import { useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, RotateCcw } from "lucide-react";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QR = "https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=2C2725&bgcolor=FCFAF8&data=" + encodeURIComponent("https://play.google.com/store/apps/details?id=com.emergent.beautyinsight.s29va4");

export default function Demo() {
  const { t, lang } = useLang();
  const s = t.styleCheck;
  const [screen, setScreen] = useState("welcome");
  const [answers, setAnswers] = useState({ occasion: "", hair: "", eyes: "", style: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const questions = [
    { key: "occasion", label: s.qOccasion, options: s.occasions },
    { key: "hair", label: s.qHair, options: s.hairs },
    { key: "eyes", label: s.qEyes, options: s.eyes },
    { key: "style", label: s.qStyle, options: s.styles },
  ];
  const complete = Object.values(answers).every(Boolean);

  const submit = async () => {
    if (!complete || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/style-check`, { ...answers, lang });
      setResult(res.data.result);
      setScreen("result");
    } catch {
      setResult(s.error);
      setScreen("result");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAnswers({ occasion: "", hair: "", eyes: "", style: "" });
    setResult("");
    setScreen("welcome");
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-6 py-10 text-center" style={{ background: "var(--surface)", color: "var(--on-surface)" }} data-testid="demo-kiosk">
      <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.2em" }}>
        <Star size={12} /> MIRA MIRROR · KIOSK DEMO
      </p>

      {screen === "welcome" && (
        <div className="mt-10 max-w-2xl" data-testid="demo-welcome">
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-tight">{s.title}</h1>
          <p className="mx-auto mt-6 max-w-md text-lg" style={{ color: "var(--muted-warm)" }}>{s.sub}</p>
          <button
            onClick={() => setScreen("quiz")}
            data-testid="demo-start-btn"
            className="pill-btn mt-12 rounded-full px-14 py-6 text-xl font-medium text-white shadow-[0_24px_48px_-16px_rgba(198,156,109,0.6)]"
            style={{ background: "var(--camel)" }}
          >
            ✦ {s.kicker}
          </button>
        </div>
      )}

      {screen === "quiz" && (
        <div className="mt-8 w-full max-w-3xl" data-testid="demo-quiz">
          <div className="space-y-8 text-left">
            {questions.map((q) => (
              <div key={q.key}>
                <p className="mb-3 text-sm font-bold uppercase" style={{ letterSpacing: "0.12em", color: "var(--muted-warm)" }}>{q.label}</p>
                <div className="flex flex-wrap gap-3">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt }))}
                      data-testid={`demo-opt-${q.key}-${q.options.indexOf(opt)}`}
                      className="rounded-full border px-6 py-3.5 text-base font-medium transition-colors duration-300"
                      style={
                        answers[q.key] === opt
                          ? { background: "var(--camel)", borderColor: "var(--camel)", color: "#fff" }
                          : { background: "var(--surface)", borderColor: "var(--line)" }
                      }
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={submit}
            disabled={!complete || loading}
            data-testid="demo-submit-btn"
            className="pill-btn mt-12 w-full rounded-full py-6 text-xl font-medium text-white disabled:opacity-40"
            style={{ background: "var(--camel)" }}
          >
            {loading ? <Loader2 size={22} className="mx-auto animate-spin" /> : <><Sparkles size={18} className="mr-2 inline" />{s.submit}</>}
          </button>
        </div>
      )}

      {screen === "result" && (
        <div className="mt-8 max-w-3xl" data-testid="demo-result">
          <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {s.resultTitle}
          </p>
          <p className="mt-6 whitespace-pre-line font-serif-display text-2xl sm:text-3xl leading-relaxed">{result}</p>
          <div className="mx-auto mt-10 w-fit rounded-3xl border p-6" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }}>
            <img src={QR} alt="QR" width={180} height={180} className="mx-auto rounded-xl" data-testid="demo-qr" />
            <p className="mt-4 max-w-[220px] text-sm font-medium" style={{ color: "var(--camel)" }}>✦ {s.ctaApp}</p>
          </div>
          <button
            onClick={reset}
            data-testid="demo-reset-btn"
            className="pill-btn mt-10 rounded-full border px-10 py-4 text-base font-medium"
            style={{ borderColor: "var(--camel)", color: "var(--camel)", background: "var(--surface)" }}
          >
            <RotateCcw size={16} className="mr-2 inline" /> {s.again}
          </button>
        </div>
      )}
    </main>
  );
}
